import { useCopilotChat } from '@copilotkit/react-core';
import { MessageRole, TextMessage } from '@copilotkit/runtime-client-gql';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/state/reduxStore';

const HERO_BAR_SEND_EVENT = 'copilot-hero-send';

/**
 * Broadcasts a message from the Contact Us hero bar. The hero bar renders inside the page routes,
 * outside the CopilotKit provider, so instead of using the chat context directly it dispatches a
 * window event that {@link useCopilotHeroBarBridge} (used inside the provider) appends to the chat.
 */
export const sendHeroBarMessage = (text: string): void => {
  window.dispatchEvent(new CustomEvent(HERO_BAR_SEND_EVENT, { detail: { content: text } }));
};

/**
 * Bridges the Contact Us hero bar to the shared chat thread by appending messages broadcast via
 * {@link sendHeroBarMessage}.
 *
 * The window listener is only attached while the hero bar is mounted, signalled by the
 * `addHeroBarListener` flag the hero bar sets in redux state.
 */
export const useCopilotHeroBarBridge = (): void => {
  const { appendMessage } = useCopilotChat();
  const addHeroBarListener = useSelector((state: RootState) => state.contactUs.addHeroBarListener);

  useEffect(() => {
    if (!addHeroBarListener) {
      return;
    }

    const handleHeroSend = (event: Event) => {
      const content = (event as CustomEvent<{ content?: string }>).detail?.content?.trim();
      if (!content) {
        return;
      }
      appendMessage(new TextMessage({ role: MessageRole.User, content })).catch((error) => {
        console.error('Failed to send message:', error);
      });
    };

    window.addEventListener(HERO_BAR_SEND_EVENT, handleHeroSend);
    return () => window.removeEventListener(HERO_BAR_SEND_EVENT, handleHeroSend);
  }, [addHeroBarListener, appendMessage]);
};

